if (!window.collectionModelMixerInitialized) {
  window.collectionModelMixerInitialized = true;

  class CollectionModelMixer {
    constructor() {
      this.states = new WeakMap();
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.mixGrids();
        });
      };
      window.addEventListener('resize', this.schedule);
      document.addEventListener('shopify:section:load', this.schedule);
      this.observer = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches('product-card, [data-model-mixing]') || node.querySelector('product-card, [data-model-mixing]'))))) this.schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.schedule();
    }

    normalize(tag) {
      const normalized = String(tag || '').toLowerCase().replace(/[\s_-]/g, '');
      return /^model[1-5]$/.test(normalized) ? normalized : '';
    }

    featuredModel(grid) {
      const rule = (grid.dataset.modelRowRules || '').split(/[\n,]+/)
        .map(line => line.split(':').map(part => part.trim()))
        .find(([handle]) => handle === grid.dataset.collectionHandle);
      return this.normalize(rule?.[1]);
    }

    stateFor(grid) {
      if (this.states.has(grid)) return this.states.get(grid);
      let previousFirst = '';
      const key = `collection-first-model:${grid.dataset.collectionHandle || location.pathname}`;
      try { previousFirst = sessionStorage.getItem(key) || ''; } catch (_) { /* Storage can be disabled. */ }
      const ranks = ['model1', 'model2', 'model3', 'model4', 'model5'];
      if (grid.dataset.modelShuffle === 'true') {
        for (let index = ranks.length - 1; index > 0; index--) {
          const swap = Math.floor(Math.random() * (index + 1));
          [ranks[index], ranks[swap]] = [ranks[swap], ranks[index]];
        }
      }
      const state = { ranks, previousFirst, key };
      this.states.set(grid, state);
      return state;
    }

    mixGrids() {
      document.querySelectorAll('[data-model-mixing="true"]').forEach(grid => {
        if (!matchMedia('(min-width: 750px)').matches && grid.dataset.modelMixMobile !== 'true') return;
        // An explicit customer sort takes precedence over presentation mixing.
        if (grid.dataset.sortBy && grid.dataset.sortBy !== 'manual') return;
        this.mixGrid(grid);
      });
    }

    mixGrid(grid) {
      if (!grid.getClientRects().length) return;
      const cards = [...grid.children].filter(card => card.matches('product-card'));
      const columns = String(getComputedStyle(grid).gridTemplateColumns.split(' ').length);
      if (grid.dataset.modelMixColumns !== columns) {
        cards.forEach(card => delete card.dataset.modelMixed);
        grid.dataset.modelMixColumns = columns;
      }
      const freshCards = cards.filter(card => card.dataset.modelMixed !== 'true');
      if (!freshCards.length) return;

      const state = this.stateFor(grid);
      const featured = this.featuredModel(grid);
      const rowOf = new Map(cards.map(card => [card, Math.round(card.getBoundingClientRect().top)]));
      const rows = [...new Set(cards.map(card => rowOf.get(card)))].sort((a, b) => a - b);
      const usedByRow = new Map(rows.map(row => [row, new Set()]));
      const groups = new Map();
      const untagged = [];
      const tagOf = card => this.normalize(card.dataset.modelTag);
      cards.forEach(card => {
        const tag = tagOf(card);
        if (card.dataset.modelMixed === 'true' && tag) usedByRow.get(rowOf.get(card)).add(tag);
      });
      freshCards.forEach(card => {
        const tag = tagOf(card);
        if (!tag) untagged.push(card);
        else {
          if (!groups.has(tag)) groups.set(tag, []);
          groups.get(tag).push(card);
        }
      });
      const recent = new Map();
      const firstPass = !cards.some(card => card.dataset.modelMixed === 'true');
      const ordered = freshCards.map((slot, index) => {
        const row = rowOf.get(slot);
        const rowIndex = rows.indexOf(row);
        const used = usedByRow.get(row);
        const available = [...groups.keys()].filter(tag => groups.get(tag).length);
        let eligible = rowIndex === 0 ? available.filter(tag => tag !== featured) : available;
        // Never hide stock or create an empty slot if the alternatives run out.
        if (!eligible.length && rowIndex === 0 && untagged.length) return untagged.shift();
        if (!eligible.length) eligible = available;
        let candidates = eligible.filter(tag => !used.has(tag));
        if (!candidates.length && untagged.length) return untagged.shift();
        if (!candidates.length) candidates = eligible;
        let tag;
        if (rowIndex === 1 && featured && !used.has(featured) && groups.get(featured)?.length) {
          tag = featured;
        } else if (candidates.length) {
          const avoidPrevious = firstPass && rowIndex === 0 && index === 0 && grid.dataset.modelShuffle === 'true';
          const alternate = avoidPrevious ? candidates.filter(candidate => candidate !== state.previousFirst) : candidates;
          if (alternate.length) candidates = alternate;
          candidates.sort((a, b) => {
            const remainingRows = rows.length - rowIndex - 1;
            const urgentA = groups.get(a).length > remainingRows;
            const urgentB = groups.get(b).length > remainingRows;
            return Number(urgentB) - Number(urgentA)
              || (recent.get(a) ?? -1) - (recent.get(b) ?? -1)
              || state.ranks.indexOf(a) - state.ranks.indexOf(b);
          });
          tag = candidates[0];
        }
        const card = tag ? groups.get(tag).shift() : untagged.shift();
        if (tag) {
          used.add(tag);
          recent.set(tag, index);
        }
        return card;
      });
      const slots = freshCards.map(card => {
        const placeholder = document.createComment('product-card-slot');
        card.replaceWith(placeholder);
        return placeholder;
      });
      ordered.forEach((card, index) => {
        card.dataset.modelMixed = 'true';
        slots[index].replaceWith(card);
      });
      if (firstPass && grid.dataset.modelShuffle === 'true') {
        const first = this.normalize(grid.querySelector(':scope > product-card')?.dataset.modelTag);
        if (first) {
          try { sessionStorage.setItem(state.key, first); } catch (_) { /* Mixing works without storage. */ }
        }
      }
    }
  }

  new CollectionModelMixer();
}
